import { defaultDataIdFromObject, InMemoryCache, type StoreObject } from "@apollo/client/core";
import { markRaw } from "vue";
import fragmentTypes from "./fragmentTypes.json";

function stableStringify(value: unknown): string {
  if (value === undefined) {
    return "undefined";
  }
  if (value == null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  return `{${Object.keys(value)
    .sort()
    .map(
      (key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`,
    )
    .join(",")}}`;
}

function sqdcEventsKeyArgs(args: Record<string, unknown> | null | undefined) {
  if (args == null) {
    return false;
  }

  const { page, ...keyArgs } = args;
  if (page != null && typeof page === "object" && !Array.isArray(page)) {
    const { cursor: _cursor, take: _take, ...pageKeyArgs } = page as Record<string, unknown>;
    return stableStringify({ ...keyArgs, page: pageKeyArgs });
  }

  return stableStringify(keyArgs);
}

export default markRaw(
  new InMemoryCache({
    possibleTypes: fragmentTypes.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          SQDCEvents: {
            keyArgs: sqdcEventsKeyArgs,
            merge(existing = [], incoming, { args }) {
              if (!args?.page?.cursor) {
                // First page → replace
                return incoming;
              }
              // Pagination → append
              return [...existing, ...incoming];
            },
          },
          meetings: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          GetSQDCBoard: {
            keyArgs: ["id"],
          },
          SQDCEventActionTypes: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      Subscription: {
        fields: {
          meetings: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      FMDocumentTileSubscription: {
        fields: {
          CustomFileConnection: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      SQDCTextTile: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
        fields: {
          sQDCTextTileColumnTypes: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      SQDCVoteTile: {
        fields: {
          // The board query omits tileId; settings queries include it.
          VoteTileConnection: { merge: true },
        },
      },
      SQDCChartTile: {
        fields: {
          // Board queries omit the connection ID; settings queries normalize it.
          chartConn: { merge: true },
        },
      },
      Role: {
        fields: {
          AccessRightRole: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      Audit: {
        keyFields: (object) => {
          if (object.revision) {
            return ["id", "revision"];
          }
          return ["id"];
        },
        fields: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
      Meeting: {
        keyFields: ["roomName"],
      },
      BDEConfigurationStatus: {
        keyFields: ["tpId"],
      },
      VoteTileConnection: {
        keyFields: ["tileId"],
      },
      TCTeamAssignment: {
        keyFields: ["userId", "date"],
      },
      TCTeamAssignmentDeviation: {
        keyFields: ["userId", "date"],
      },
      BDEMachineStatus: {
        keyFields: ["tpId"],
      },
      SQDCBoardLayoutXYConfig: {
        keyFields: ["x", "y", "boardId"],
      },
      SQDCBoardLayoutXYZConfig: {
        keyFields: ["x", "y", "z", "boardId"],
      },
      SQDCBoardLayout: {
        keyFields: ["x", "y", "z", "a", "i", "BoardId"],
      },
      SQDCKeyAdditionalSettings: {
        keyFields: ["tileId"],
      },
      SQDCKeyAdditionalSettingsCostCenters: {
        keyFields: ["ccId", "tileId"],
      },
      SQDCBoardAccessBoardVisibility: {
        keyFields: ["boardAccessId", "boardId"],
      },
      Site: {
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming);
        },
      },
      TCVacationListEntry: {
        keyFields: () => false,
      },
      ChartCommonSeriesType: {
        keyFields: false,
      },
      ChartAggregation: {
        keyFields: false,
      },
      ChartSeriesWithoutData: {
        keyFields: ["key"],
      },
      MQTTDevice: {
        keyFields: (object: Readonly<StoreObject>) => {
          return defaultDataIdFromObject({ __typename: object.__typename, id: object.deviceId });
        },
      },
      SQDCBoard: {
        keyFields: ["id"],
        fields: {
          // Board queries return a complete layout list for their arguments.
          layouts: { merge: false },
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
      SQDCEvent: {
        fields: {
          // Each response supplies the current list; removed flags and limited revision
          // windows must not be combined with entries from an earlier response.
          FlagEventConnection: { merge: false },
          SQDCEventRevisions: { merge: false },
        },
      },
      User: {
        keyFields: (object: Readonly<StoreObject>) => {
          if ("HLPIdeaUserConnection" in object) {
            return false;
          }
          return defaultDataIdFromObject(object);
        },
      },
      GroupedTarget: {
        keyFields: (object: Readonly<StoreObject>) => {
          // @ts-expect-error types not correct
          const costCenterId = object.costCenter?.id;
          // @ts-expect-error types not correct
          const typeId = object.type?.id;
          if (costCenterId == null || typeId == null) {
            return false;
          }
          return `GroupedTarget:${costCenterId}|${typeId}`;
        },
      },
      GroupedTargetValue: {
        keyFields: (object: Readonly<StoreObject>) => {
          if (object.id == null) {
            return false;
          }
          return `Target:${object.id}`;
        },
      },
      DBWidget: {
        keyFields: (object: Readonly<StoreObject>) => {
          return `DBWidget:${object.type}`;
        },
      },
    },
  }),
);
